import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { DatePickerComponent } from '@syncfusion/ej2-angular-calendars';
import { Subscription } from 'rxjs';
import { fadeInOut } from '../../shared/helpers/animations';
import { ILLFormDetail, ILLGenericKeys, ILLKit, ILLProduct } from '../../shared/models/index';
import { LLFormService, StorageManagerService } from '../../shared/services/index';
import { Utilities } from '../../shared/services/utilities';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'app-create-ll-form',
    templateUrl: './create-ll-form.component.html',
    styleUrls: ['./create-ll-form.component.scss'],
    animations: [fadeInOut],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class CreateLLFormComponent implements OnInit, OnDestroy {

  @ViewChild('effDate') public effectiveDate: DatePickerComponent;

  sub: Subscription;
  public wizardForm: UntypedFormGroup;

  errorMessage: string;
  selectedProductType: string;
  submitted = false;
  loading = false; productTypeId: any;
  productTypesLov: ILLGenericKeys[] = [];
  productTypesDataSource: any[] = [];
  public dateValue: Date = new Date();
  llForm: ILLFormDetail = new ILLFormDetail();
  dateFormat: any;

  // set the height of the popup element
  public ddwHeight = '220px';

  // maps the appropriate column to fields property
  // eslint-disable-next-line @typescript-eslint/member-ordering
  public productTypeFields: Object = {
    text: 'label',
    value: 'id'
  };

  // set the placeholder to DropDownList input elements
  // eslint-disable-next-line @typescript-eslint/member-ordering
  public productTypeWaterMark = 'Select a Product Type';


  // eslint-disable-next-line @typescript-eslint/member-ordering
  formErrors = {
    formName: '',
    productTypeId: ''
  };

  // eslint-disable-next-line @typescript-eslint/member-ordering
  validationMessages = {
    formName: {
      required: 'Form name is required'
    },
    productTypeId: {
      required: 'Product type is required'
    }
  };


  constructor (
    private fb: UntypedFormBuilder, private router: Router,
    private llService: LLFormService, private toastr: ToastrService,
    private utilService: Utilities, private storageManagerService: StorageManagerService) { }


  ngOnInit(): void {
    this.wizardForm = this.fb.group({
      formName: ['', { validators: [Validators.required], updateOn: 'blur' }],
      productTypeId: ['', { validators: [Validators.required], updateOn: 'blur' }],
      effectiveDate: ['', { updateOn: 'blur' }],
      isKit: ['No', { updateOn: 'blur' }]
    });

    this.initParams();

    // Watches changes on the entire form
    this.wizardForm.valueChanges.subscribe(data => {

      this.logValidationErrors(this.wizardForm);
    });

    this.dateFormat = Utilities.getLocaleDateString();

    // set focus on the invisible div so the first tab will bring it to the "Skip to Main Navigation" button because it must be the first tab target on the page (508 Compliance)
    this.initTabIndex();
  }

  initTabIndex() {
    (document.activeElement as HTMLElement).blur();

    const main = document.getElementById("firstDiv");

    if (main) {
      main.focus();
    }
  }

  initParams() {
    this.wizardForm.reset();
    // disable the company dropdown to prevent the user from switching client on the form
    this.utilService.toggleClientDropdownState(true);
    this.getProductTypes();
    this.getFormControlByName('isKit').patchValue('No');
}

  // Retrieve list of all the available product types
  getProductTypes(): void {
    this.llService.getProductTypes().subscribe(data => {
      if (data) {
        this.productTypesLov = data['data'].filter(x => x.lsP_FORM_TYPE === 'doc_type_ll');

        this.productTypesLov.forEach((type: ILLGenericKeys) => {

          const ds = { id: 0, label: '' };
          ds.id = +type.id;
          ds.label = type.label + ' (' + type.code + ')';

          this.productTypesDataSource.push(ds);
        });
      }
    },
      error => {
        this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
      }
    );
  }

  productTypeSelectionChanged(event) {
    if (event.itemData) {
      this.productTypeId = event.value;

      this.selectedProductType = this.getProductTypeDesc(this.productTypeId);
    }
  }

  // convenience getter for easy access to form fields
  get f() { return this.wizardForm.controls; }

  // Save/Update contact info to the database
  createLLForm(): void {

    // stop here if form is invalid
    if (this.wizardForm.invalid) {

     if (!this.f.formName.value) {

       this.formErrors.formName = this.validationMessages.formName.required;
      }

      if (!this.f.productTypeId.value) {

        this.formErrors.productTypeId = this.validationMessages.productTypeId.required;
      }

      return;
    }

    if (this.wizardForm.valid) {

      this.loading = true;

      if (this.wizardForm.dirty) {

        this.llForm.formName = this.wizardForm.value.formName;
        this.llForm.effectiveDate = this.effectiveDate.value;
        this.llForm.splVersion = '1';
        this.llForm.serviceType = this.productTypesLov.find(p => p.id === this.productTypeId);
        if (this.llForm.serviceType) {
              this.llForm.serviceType.label =`${this.llForm.serviceType.label} (${this.llForm.serviceType.code})`;
        }

        if (this.wizardForm.value.isKit === 'No') {

          const product = new ILLProduct();

          product.proprietaryName = '';
          product.$type = 'RTCD_LLProduct';

          this.llForm.products = [];
          this.llForm.products.push(product);
        }

        if (this.wizardForm.value.isKit === 'Yes') {

          const kit = new ILLKit();
          kit.parts = [];
          let part = new ILLProduct();
          part.$type = "RTCD_LLPart";
          kit.parts.push(part);  // we need to create an empty part initially
          kit.$type = 'RTCD_LLKit';
          this.llForm.products = [];
          this.llForm.products.push(kit);
        }

        const modifiedByUser = this.storageManagerService.read('userData').name;

        const newProductListing: Object = {
          form: this.llForm,
          kit: this.wizardForm.value.isKit
        };

        this.llService.saveLLForm(this.llForm, modifiedByUser, true).subscribe((response) => {
          this.loading = false;
          this.router.navigate([`/products/ll-form/${ response?.data?.llRevisionId }`]);
        },
          error => {
            this.loading = false;
            this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
          }
        );
      }
    }
  }

  getProductTypeDesc(id: string): string {
    return this.productTypesLov.filter(x => x.id === id)[0].label;
  }

  onOpen(e: any) {
    this.wizardForm.markAsDirty();
    this.wizardForm.markAsTouched();
  }

  cancelBtnClicked() {
    this.router.navigateByUrl('/products/portfolio');
  }

  // Get a form control by name
  getFormControlByName(controlName: string): UntypedFormControl {
    return <UntypedFormControl>this.wizardForm.get(controlName);
  }

  onBlur() {

    this.wizardForm.patchValue({
      formName: this.wizardForm.value.formName ? this.wizardForm.value.formName.trim() : ''
    });
  }

  ngOnDestroy(): void {
    this.utilService.toggleClientDropdownState(false);
  }

  /**
     * The function drills to down from the FormGroup or FormArray down
     * to the AbstractControl in order to check if the value is entered.
     * Populates the FormErrors object based on the Validators attached to the formControls.
     * @param {FormGroup} group passed by the form validation.
     */
  logValidationErrors(group: UntypedFormGroup = this.wizardForm): void {
    Object.keys(group.controls).forEach((key: string) => {
      const abstractControl = group.get(key);

      this.formErrors[key] = '';
      if (
        abstractControl &&
        !abstractControl.valid &&
        (abstractControl.touched || abstractControl.dirty)
      ) {
        const messages = this.validationMessages[key];

        for (const errorKey in abstractControl.errors) {
          if (errorKey) {
            this.formErrors[key] += messages[errorKey] + ' ';
          }
        }
      }

      if (abstractControl instanceof UntypedFormGroup) {
        this.logValidationErrors(abstractControl);
      }

      // We need this additional check to get to the FormGroup
      // in the FormArray and then recursively call this
      // logValidationErrors() method to fix the broken validation
      if (abstractControl instanceof UntypedFormArray) {
        for (const control of abstractControl.controls) {
          if (control instanceof UntypedFormGroup) {
            this.logValidationErrors(control);
          }
        }
      }
    });
  }
}


